// routes/_app.$usernameRaw.manga.($subTab).tsx
//
// `/{lang}/@username/manga[/:subTab]` — a legacy URL that has been
// deprecated since 2025-07-23 (`PersonalMangaList` / the `'manga'`
// `WorkTypes` branch in `UserProfileContent.mapPathToWorkType` are
// both commented out). The previous CSR-only implementation
// (`dynamic({ssr:false})` `UserProfileContent` behind a
// `<UserProfileShell>` Suspense fallback) shipped the shell from
// SSR, then mounted `<UserProfileContent>` on the client which
// immediately tried to `<Navigate to="/@user/artworks/manga" />`
// — but the `/artworks/:subTab='manga'` path now lands in
// `shouldNotHappen()` because `mapPathToWorkType` no longer
// recognises `'manga'`, so the SPA navigation surfaced a blank
// error-boundary page anyway. Either way the legacy URL was
// effectively broken.
//
// New behaviour: respond with a server-side `302` straight to the
// canonical artworks root (`/{lang}/@username/artworks`). The
// optional `:subTab` segment is dropped because the only
// `:subTab='manga'` consumer was the now-deleted
// `PersonalMangaList`, and keeping the segment would just route
// the redirect target to the broken sub-tab again. Query string
// and hash are preserved so deep-link parameters survive the bounce.
import { redirect } from 'react-router'
import { userProfileHandle, userProfileLoader } from 'src/utils/router/route'
import type { Route } from './+types/_app.$usernameRaw.manga.($subTab)'

export const handle = userProfileHandle

/**
 * Build the canonical `/{lang}/@username/artworks` URL, preserving
 * query and hash but dropping the optional `:subTab` segment (the
 * only `:subTab='manga'` consumer was the now-deleted
 * `PersonalMangaList` — see file-level comment).
 */
function buildArtworksRedirectUrl(request: Request): string {
  const url = new URL(request.url)
  url.pathname = url.pathname.replace(
    /^(\/.*?\/@[^/]+)\/manga(?:\/[^/]+)?\/?$/,
    '$1/artworks',
  )
  return `${url.pathname}${url.search}${url.hash}`
}

export async function loader({ request }: Route.LoaderArgs) {
  throw redirect(buildArtworksRedirectUrl(request))
}

export async function clientLoader({
  request,
  params,
}: Route.ClientLoaderArgs) {
  await userProfileLoader({
    request,
    params: { ...params, tab: 'manga' },
  })
  throw redirect(buildArtworksRedirectUrl(request))
}
clientLoader.hydrate = true

/**
 * Placeholder element — never actually mounted because both `loader`
 * and `clientLoader` `throw redirect(...)` before React Router gets a
 * chance to render this route. The CSR data router
 * (`src/data-router/routes.tsx`) still references `module.default` as
 * the route's `element`, so we have to export *something*.
 */
export default function UserMangaRedirect() {
  return null
}
